import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import { Map, Source, Layer, Popup, Marker } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import axiosInstance from 'utils/axiosUtil';
import axiosRaw from 'axios';
import fileDownload from 'js-file-download';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ApartmentIcon from '@mui/icons-material/Apartment';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import HubIcon from '@mui/icons-material/Hub';
import GetAppIcon from '@mui/icons-material/GetApp';
import { StoreUtil, LocationTypeUtil } from 'doctivity-shared/utils';
import { withRouter } from 'utils';
import { OrganizationsTabs } from './OrganizationsTabs';
import { ServiceLineSelect } from 'components';

import { queryMigrations } from 'store/actions/migrationsActions';

const mapboxToken =
  'pk.eyJ1IjoiZG9jdGl2aXR5IiwiYSI6ImNsMmo2OWx6bTBtdmUza3J6eDQ2Z3pvdG0ifQ.o9j6xJxOa2WKQLYdqQaJRQ';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  mapContainer: {
    width: '100%',
    height: 'calc(100vh - 340px)',
    minHeight: 500,
    position: 'relative',
    borderRadius: 8,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  title: {
  },
  pdfButton: {
    marginLeft: theme.spacing(1),
  },
  pdfIcon: {
    fontSize: 16,
    fontWeight: 200,
  },
  spacer: {
    flex: 1,
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterDropDown: {
    minWidth: 200,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  viewModeToggle: {
    backgroundColor: '#fff',
    height: 56,
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      padding: '0 12px',
      fontSize: '0.875rem',
      height: '100%',
    },
    '& .Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  searchField: {
    width: 300,
    backgroundColor: '#fff',
    borderRadius: 4,
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#ddd',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 500,
  },
  legendContainer: {
    position: 'absolute',
    bottom: 30,
    right: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: theme.spacing(1.5),
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1,
  },
  legendTitle: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 8,
    color: '#333',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
    fontSize: 11,
    color: '#666',
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 3,
    border: '1px solid rgba(0,0,0,0.1)',
  },
  disclaimer: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
  },
  popup: {
    '& .mapboxgl-popup-content': {
      padding: theme.spacing(1.5, 2),
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  popupCounty: {
    fontWeight: 700,
    fontSize: 15,
    marginBottom: 4,
    color: '#222',
  },
  popupRow: {
    fontSize: 13,
    color: '#555',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  popupHint: {
    fontSize: 11,
    color: '#888',
    marginTop: 8,
    fontStyle: 'italic',
  },
  orgMarker: {
    borderRadius: '50%',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'scale(1.2)',
    },
  },
  orgPopup: {
    '& .mapboxgl-popup-content': {
      padding: theme.spacing(1.5, 2),
      borderRadius: 8,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      minWidth: 200,
    },
  },
  orgPopupName: {
    fontWeight: 700,
    fontSize: 14,
    marginBottom: 4,
    color: '#222',
  },
  orgPopupRow: {
    fontSize: 12,
    color: '#555',
    marginBottom: 2,
  },
  orgPopupLink: {
    fontSize: 12,
    color: theme.palette.primary.main,
    marginTop: 8,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  selectedCountyBanner: {
    position: 'absolute',
    top: 10,
    left: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  selectedCountyText: {
    fontWeight: 600,
    fontSize: 14,
  },
  clearButton: {
    fontSize: 12,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  instructions: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: theme.spacing(3, 4),
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center',
    zIndex: 1,
    maxWidth: 400,
  },
  instructionsTitle: {
    fontWeight: 600,
    fontSize: 18,
    marginBottom: 8,
    color: '#333',
  },
  instructionsText: {
    fontSize: 14,
    color: '#666',
  },
});

// Pennsylvania center coordinates
const DEFAULT_CENTER = {
  longitude: -82.5,
  latitude: 40.5,
  zoom: 7,
};

// Color for counties with data
const COUNTY_FILL_COLOR = '#4A90D9';
const COUNTY_HOVER_COLOR = '#2E6AB3';
const COUNTY_SELECTED_COLOR = '#2082F4';
const COUNTY_HAS_DATA_COLOR = '#2082F4';

// Color scale for patient counts (same as OrganizationMigrationHeatMap but starting darker)
const colorScale = [
  '#FFDD6A', // yellow (start darker)
  '#FFCE6A', // yellow-orange
  '#FFBF69', // orange
  '#FFA06E', // peachy orange
  '#FF8073', // lighter coral
  '#FF6178', // coral red
  '#E84855', // darker red
  '#D62839', // darkest red
];

// Get color based on patient count relative to max
const getPatientColor = (patients, maxPatients) => {
  if (!maxPatients || maxPatients === 0) return colorScale[0];
  const ratio = Math.min(patients / maxPatients, 1);
  const index = Math.floor(ratio * (colorScale.length - 1));
  return colorScale[index];
};

class PatientMigrationPage extends React.Component {
  constructor(props) {
    super(props);

    // Load saved settings from localStorage
    const savedServiceline = localStorage.getItem('patientMigration.serviceline');
    const savedCareSetting = localStorage.getItem('patientMigration.careSetting');
    const savedViewState = localStorage.getItem('patientMigration.viewState');

    let viewState = DEFAULT_CENTER;
    if (savedViewState) {
      try {
        viewState = JSON.parse(savedViewState);
      } catch (e) {
        // ignore parse error
      }
    }

    const savedViewMode = localStorage.getItem('patientMigration.viewMode');

    this.state = {
      countiesGeoJSON: null,
      populationData: null,
      countyMigrationData: {}, // geo_id -> true (counties with data)
      originCountyData: {}, // geo_id -> { total_patients } for heat map
      loading: true,
      viewState,
      hoveredCounty: null,
      popupInfo: null,
      selectedCounty: null,
      organizations: [],
      loadingOrgs: false,
      orgPopupInfo: null,
      searchQuery: '',
      serviceline: savedServiceline ? (savedServiceline === 'all' ? 'all' : parseInt(savedServiceline, 10)) : 'all',
      careSetting: savedCareSetting ? parseInt(savedCareSetting, 10) : 0,
      viewMode: savedViewMode || 'destinations', // 'destinations' or 'origins'
    };
  }

  componentDidMount() {
    this.loadCountiesGeoJSON();
    this.loadPopulationData();
    // Only fetch hasData highlighting if specific serviceline selected
    if (this.state.serviceline && this.state.serviceline !== 'all') {
      this.fetchCountyMigrationData();
    }
  }

  loadCountiesGeoJSON = () => {
    axiosRaw.get('maps/counties_patientmigration.geojson')
      .then((response) => {
        this.setState({ countiesGeoJSON: response.data });
      })
      .catch((error) => {
        console.error('Failed to load county boundaries:', error);
      });
  };

  loadPopulationData = () => {
    axiosRaw.get('maps/county-population.json')
      .then((response) => {
        this.setState({ 
          populationData: response.data,
          loading: false,
        });
      })
      .catch((error) => {
        console.error('Failed to load population data:', error);
        this.setState({ 
          populationData: {},
          loading: false,
        });
      });
  };

  fetchCountyMigrationData = () => {
    const { serviceline, careSetting, viewMode } = this.state;
    const { clientId } = this.props;

    // Skip hasData highlighting query if "All" servicelines - too slow
    if (!serviceline || serviceline === 'all') {
      this.setState({ countyMigrationData: {} });
      return;
    }

    const params = {
      serviceline_id: serviceline,
      selected_client_id: clientId,
    };

    if (careSetting && careSetting !== 0) {
      params.location_type = careSetting;
    }

    // Use different endpoint based on view mode
    const endpoint = viewMode === 'origins' 
      ? '/PatientMigration/destination-county-summary'
      : '/PatientMigration/county-summary';

    axiosInstance.get(endpoint, { params })
      .then((response) => {
        const data = response.data?.data || {};
        this.setState({ countyMigrationData: data });
      })
      .catch((error) => {
        console.error('Failed to load county migration data:', error);
        this.setState({ countyMigrationData: {} });
      });
  };

  handleMapMove = (evt) => {
    this.setState({ viewState: evt.viewState });
    
    // Save to localStorage
    localStorage.setItem('patientMigration.viewState', JSON.stringify({
      longitude: evt.viewState.longitude,
      latitude: evt.viewState.latitude,
      zoom: evt.viewState.zoom,
    }));
  };

  handleMapHover = (event) => {
    const features = event.features;
    if (features && features.length > 0) {
      const feature = features[0];
      let geoid = feature.properties?.GEOID;
      const countyName = feature.properties?.NAME;

      const state = feature.properties?.STATE_ABBR || feature.properties?.STATEFP;
      
      if (countyName && geoid) {
        const population = this.state.populationData?.[geoid] || 0;
        const { viewMode, originCountyData, selectedCounty } = this.state;
        
        // Get patient count if in origins mode and this is an origin county
        const originData = viewMode === 'origins' && selectedCounty && originCountyData[geoid];
        
        this.setState({
          hoveredCounty: geoid,
          popupInfo: {
            longitude: event.lngLat.lng,
            latitude: event.lngLat.lat,
            county_name: countyName,
            state: this.getStateAbbr(feature.properties?.STATEFP),
            geoid,
            population,
            // Add origin patient data if available
            originPatients: originData?.total_patients || null,
            originOrgCount: originData?.organization_count || null,
          },
        });
      }
    } else {
      this.setState({
        hoveredCounty: null,
        popupInfo: null,
      });
    }
  };

  handleMapLeave = () => {
    this.setState({
      hoveredCounty: null,
      popupInfo: null,
    });
  };

  handleMapClick = (event) => {
    console.log('Map clicked', event);
    const features = event.features;
    console.log('Features:', features);
    if (features && features.length > 0) {
      const feature = features[0];
      let geoid = feature.properties?.GEOID;
      const countyName = feature.properties?.NAME;

      console.log('County clicked:', countyName, geoid);
      
      if (geoid && countyName) {
        this.setState({
          selectedCounty: {
            geoid,
            name: countyName,
            state: this.getStateAbbr(feature.properties?.STATEFP),
          },
          popupInfo: null,
          organizations: [], // Clear existing org markers immediately
          originCountyData: {}, // Clear previous origin data
        }, () => {
          console.log('State updated, fetching data for:', geoid);
          this.fetchDataForCounty(geoid);
        });
      }
    }
  };

  getStateAbbr = (fips) => {
    const stateMap = {
      '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA',
      '08': 'CO', '09': 'CT', '10': 'DE', '11': 'DC', '12': 'FL',
      '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL', '18': 'IN',
      '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME',
      '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS',
      '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH',
      '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
      '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI',
      '45': 'SC', '46': 'SD', '47': 'TN', '48': 'TX', '49': 'UT',
      '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV', '55': 'WI',
      '56': 'WY',
    };
    return stateMap[fips] || fips;
  };

  fetchOrganizationsForCounty = (geoid) => {
    const { serviceline, careSetting } = this.state;
    const { clientId } = this.props;

    this.setState({ loadingOrgs: true });

    const params = {
      geo_id: geoid,
      selected_client_id: clientId,
    };

    // Only add serviceline_id if not 'all'
    if (serviceline && serviceline !== 'all') {
      params.serviceline_id = serviceline;
    }

    if (careSetting && careSetting !== 0) {
      params.location_type = careSetting;
    }

    axiosInstance.get('/PatientMigration/organizations-by-county', { params })
      .then((response) => {
        const orgs = response.data?.data || [];
        this.setState({
          organizations: orgs.map(org => ({
            id: org.id,
            name: org.name,
            doingBusinessAs: org.doing_business_as,
            latitude: org.location?.latitude,
            longitude: org.location?.longitude,
            city: org.location?.city,
            state: org.location?.state,
            patients: org.total_patients,
            isAffiliated: org.is_affiliated,
          })).filter(org => org.latitude && org.longitude), // Only show orgs with coordinates
          loadingOrgs: false,
        });
      })
      .catch((error) => {
        console.error('Failed to fetch organizations for county:', error);
        this.setState({
          organizations: [],
          loadingOrgs: false,
        });
      });
  };

  clearSelectedCounty = () => {
    this.setState({
      selectedCounty: null,
      organizations: [],
      orgPopupInfo: null,
    });
  };

  handleOrgMarkerHover = (org) => {
    this.setState({
      orgPopupInfo: org,
    });
  };

  handleOrgMarkerLeave = () => {
    this.setState({
      orgPopupInfo: null,
    });
  };

  handleOrgClick = (org) => {
    // Open organization in new window
    window.open(`/organizations/${org.id}`, '_blank');
  };

  handleSearchChange = (event) => {
    this.setState({ searchQuery: event.target.value });
  };

  handleSearchSubmit = (event) => {
    if (event.key === 'Enter') {
      // Use Mapbox geocoding to find the address
      const query = encodeURIComponent(this.state.searchQuery);
      axiosRaw.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${mapboxToken}&country=US`)
        .then((response) => {
          if (response.data.features && response.data.features.length > 0) {
            const [lng, lat] = response.data.features[0].center;
            this.setState({
              viewState: {
                ...this.state.viewState,
                longitude: lng,
                latitude: lat,
                zoom: 9,
              },
            });
          }
        })
        .catch((error) => {
          console.error('Geocoding failed:', error);
        });
    }
  };

  onServicelineChange = (id) => {
    // Save to localStorage
    if (id) {
      localStorage.setItem('patientMigration.serviceline', id);
    } else {
      localStorage.removeItem('patientMigration.serviceline');
    }
    
    this.setState({ serviceline: id }, () => {
      // Fetch county migration data for highlighting
      this.fetchCountyMigrationData();
      // Re-fetch organizations if a county is selected
      if (this.state.selectedCounty) {
        this.fetchDataForCounty(this.state.selectedCounty.geoid);
      }
    });
  };

  onCareSettingChange = (event) => {
    const value = event.target.value;
    
    // Save to localStorage
    localStorage.setItem('patientMigration.careSetting', value);
    
    this.setState({ careSetting: value }, () => {
      // Fetch county migration data for highlighting
      this.fetchCountyMigrationData();
      // Re-fetch data if a county is selected
      if (this.state.selectedCounty) {
        this.fetchDataForCounty(this.state.selectedCounty.geoid);
      }
    });
  };

  onViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      localStorage.setItem('patientMigration.viewMode', newMode);
      this.setState({ 
        viewMode: newMode,
        selectedCounty: null,
        organizations: [],
        originCountyData: {},
      }, () => {
        this.fetchCountyMigrationData();
      });
    }
  };

  fetchDataForCounty = (geoid) => {
    const { viewMode } = this.state;
    if (viewMode === 'destinations') {
      this.fetchOrganizationsForCounty(geoid);
    } else {
      this.fetchOriginCounties(geoid);
    }
  };

  fetchOriginCounties = (geoid) => {
    const { serviceline, careSetting } = this.state;
    const { clientId } = this.props;

    this.setState({ loadingOrgs: true });

    const params = {
      geo_id: geoid,
      selected_client_id: clientId,
    };

    // Only add serviceline_id if not 'all'
    if (serviceline && serviceline !== 'all') {
      params.serviceline_id = serviceline;
    }

    if (careSetting && careSetting !== 0) {
      params.location_type = careSetting;
    }

    axiosInstance.get('/PatientMigration/origin-counties', { params })
      .then((response) => {
        const data = response.data?.data || {};
        this.setState({
          originCountyData: data,
          loadingOrgs: false,
        });
      })
      .catch((error) => {
        console.error('Failed to fetch origin counties:', error);
        this.setState({
          originCountyData: {},
          loadingOrgs: false,
        });
      });
  };

  handleExportCSV = () => {
    const { selectedCounty, viewMode, organizations, originCountyData, serviceline, careSetting } = this.state;
    
    if (!selectedCounty) {
      return;
    }

    const timestamp = new Date().toISOString().split('T')[0];
    
    if (viewMode === 'destinations') {
      // Export organizations data
      if (!organizations || organizations.length === 0) {
        return;
      }
      
      const headers = ['Organization', 'City', 'State', 'Captured Patients', 'Affiliated'];
      const rows = organizations.map(org => [
        org.name,
        org.city || '',
        org.state || '',
        org.patients || 0,
        org.isAffiliated ? 'Yes' : 'No',
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      const filename = `patient_migration_${selectedCounty.name}_${selectedCounty.state}_orgs_${timestamp}.csv`;
      fileDownload(csvContent, filename);
      
    } else {
      // Export origin counties data
      const originEntries = Object.values(originCountyData);
      if (!originEntries || originEntries.length === 0) {
        return;
      }
      
      const headers = ['County', 'State', 'Captured Patients', 'Organization Count'];
      const rows = originEntries.map(entry => [
        entry.county_name,
        entry.state,
        entry.total_patients || 0,
        entry.organization_count || 0,
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      const filename = `patient_migration_${selectedCounty.name}_${selectedCounty.state}_origins_${timestamp}.csv`;
      fileDownload(csvContent, filename);
    }
  };

  render() {
    const { classes } = this.props;
    const {
      countiesGeoJSON,
      loading,
      viewState,
      hoveredCounty,
      popupInfo,
      selectedCounty,
      organizations,
      loadingOrgs,
      orgPopupInfo,
      searchQuery,
      serviceline,
      careSetting,
      countyMigrationData,
      originCountyData,
      viewMode,
    } = this.state;

    if (loading || !countiesGeoJSON) {
      return (
        <>
          <OrganizationsTabs tab='patient_migration' />
          <div className={classes.container}>
            <Card>
              <CardContent>
                <Box className={classes.loadingContainer}>
                  <CircularProgress />
                </Box>
              </CardContent>
            </Card>
          </div>
        </>
      );
    }

    // Filter expression for hovering
    const hoverFilter = hoveredCounty 
      ? ['==', ['get', 'GEOID'], hoveredCounty]
      : ['==', ['get', 'GEOID'], ''];

    // Filter expression for selected county
    const selectedFilter = selectedCounty
      ? ['==', ['get', 'GEOID'], selectedCounty.geoid]
      : ['==', ['get', 'GEOID'], ''];

    // Filter for counties that have migration data
    const countiesWithData = Object.keys(countyMigrationData);
    const hasDataFilter = countiesWithData.length > 0
      ? ['in', ['get', 'GEOID'], ['literal', countiesWithData]]
      : ['==', ['get', 'GEOID'], ''];

    // For origins mode: create filter and color expression for heat map
    const originCounties = Object.keys(originCountyData);
    const originCountiesFilter = originCounties.length > 0
      ? ['in', ['get', 'GEOID'], ['literal', originCounties]]
      : ['==', ['get', 'GEOID'], ''];

    // Calculate max patients for color scaling
    const maxPatients = Math.max(1, ...Object.values(originCountyData).map(d => d.total_patients || 0));
    
    // Build color expression for heat map using same colorScale as org markers
    const originColorExpression = originCounties.length > 0
      ? [
          'match',
          ['get', 'GEOID'],
          ...originCounties.flatMap(geoId => {
            const patients = originCountyData[geoId]?.total_patients || 0;
            const ratio = Math.min(patients / maxPatients, 1);
            const colorIndex = Math.floor(ratio * (colorScale.length - 1));
            return [geoId, colorScale[colorIndex]];
          }),
          'rgba(0,0,0,0)' // default
        ]
      : 'rgba(0,0,0,0)';

    return (
      <>
        <OrganizationsTabs tab='patient_migration' />
        <div className={classes.container}>
          <Card>
            <CardContent>
              <div className={classes.header}>
                <Typography 
                  component='h2' 
                  variant='h6' 
                  color='primary'
                  className={classes.title}
                >
                  Patient Migration
                </Typography>
                {selectedCounty && (
                  <div className={classes.pdfButton}>
                    <IconButton onClick={this.handleExportCSV} size="large">
                      <GetAppIcon className={classes.pdfIcon} />
                    </IconButton>
                  </div>
                )}
                <div className={classes.spacer} />
                <TextField
                  className={classes.searchField}
                  placeholder="Search address or location..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={this.handleSearchChange}
                  onKeyPress={this.handleSearchSubmit}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>

              <div className={classes.filterContainer}>
                <ServiceLineSelect
                  value={serviceline}
                  onChange={this.onServicelineChange}
                  showSubServicelines
                  showAll
                />
                <FormControl
                  variant='outlined'
                  className={classes.filterDropDown}
                >
                  <InputLabel>Care Setting</InputLabel>
                  <Select
                    value={careSetting}
                    onChange={this.onCareSettingChange}
                    label='Care Setting'
                  >
                    {LocationTypeUtil.getAll()
                      .filter((type) => !type.codesOnly)
                      .map((type) => (
                        <MenuItem value={type.id} key={type.id}>
                          {type.id === 0 ? <em>{type.name}</em> : type.name}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={this.onViewModeChange}
                  className={classes.viewModeToggle}
                >
                  <ToggleButton value="destinations">
                    <TrendingFlatIcon style={{ marginRight: 4 }} />
                    Where Patients Go
                  </ToggleButton>
                  <ToggleButton value="origins">
                    <TrendingFlatIcon style={{ marginRight: 4, transform: 'rotate(180deg)' }} />
                    Where Patients Live
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              {/* Disclaimer above map */}
              {selectedCounty && (
                <Typography className={classes.disclaimer} style={{ textAlign: 'center', marginBottom: 8 }}>
                  {viewMode === 'destinations'
                    ? `Organizations where residents of ${selectedCounty.name} County, ${selectedCounty.state} seek care.`
                    : `Counties where patients of organizations in ${selectedCounty.name} County, ${selectedCounty.state} reside.`
                  }
                </Typography>
              )}

            <div className={classes.mapContainer}>
              <Map
                {...viewState}
                onMove={this.handleMapMove}
                onMouseMove={this.handleMapHover}
                onMouseLeave={this.handleMapLeave}
                onClick={this.handleMapClick}
                mapboxAccessToken={mapboxToken}
                mapStyle="mapbox://styles/mapbox/light-v11"
                interactiveLayerIds={['county-fills']}
                attributionControl={false}
              >
                {/* All counties base layer */}
                <Source id="counties" type="geojson" data={countiesGeoJSON}>
                  {/* Base fill for all counties - transparent, just for interactivity */}
                  <Layer
                    id="county-fills"
                    type="fill"
                    paint={{
                      'fill-color': '#000000',
                      'fill-opacity': 0,
                    }}
                  />

                  {/* Counties with migration data - highlighted */}
                  <Layer
                    id="county-has-data"
                    type="fill"
                    filter={hasDataFilter}
                    paint={{
                      'fill-color': COUNTY_HAS_DATA_COLOR,
                      'fill-opacity': 0.15,
                    }}
                  />

                  {/* Origin counties heat map (only in origins mode) */}
                  {viewMode === 'origins' && selectedCounty && originCounties.length > 0 && (
                    <Layer
                      id="county-origins-heatmap"
                      type="fill"
                      filter={originCountiesFilter}
                      paint={{
                        'fill-color': originColorExpression,
                        'fill-opacity': 0.7,
                      }}
                    />
                  )}
                  
                  {/* Hover highlight */}
                  <Layer
                    id="county-hover"
                    type="fill"
                    filter={hoverFilter}
                    paint={{
                      'fill-color': COUNTY_HOVER_COLOR,
                      'fill-opacity': 0.6,
                    }}
                  />

                  {/* Selected county highlight */}
                  <Layer
                    id="county-selected"
                    type="fill"
                    filter={selectedFilter}
                    paint={{
                      'fill-color': COUNTY_SELECTED_COLOR,
                      'fill-opacity': 0.7,
                    }}
                  />
                  
                  {/* County outlines */}
                  <Layer
                    id="county-outlines"
                    type="line"
                    paint={{
                      'line-color': '#888888',
                      'line-width': 1,
                      'line-opacity': 0.5,
                    }}
                  />
                </Source>

                {/* Organization markers (only in destinations mode) */}
                {viewMode === 'destinations' && selectedCounty && (() => {
                  const maxPatients = Math.max(...organizations.map(o => o.patients || 0), 1);
                  return organizations
                    .slice()
                    // Sort: non-affiliated by patient count first, then affiliated (always on top)
                    .sort((a, b) => {
                      if (a.isAffiliated && !b.isAffiliated) return 1;
                      if (!a.isAffiliated && b.isAffiliated) return -1;
                      return (a.patients || 0) - (b.patients || 0);
                    })
                    .map((org) => {
                      const patients = org.patients || 0;
                      return (
                        <Marker
                          key={org.id}
                          longitude={org.longitude}
                          latitude={org.latitude}
                          anchor="center"
                        >
                          <div
                            className={classes.orgMarker}
                            style={{
                              backgroundColor: getPatientColor(patients, maxPatients),
                            }}
                            onMouseEnter={() => this.handleOrgMarkerHover(org)}
                            onMouseLeave={this.handleOrgMarkerLeave}
                            onClick={() => this.handleOrgClick(org)}
                          >
                            {org.isAffiliated ? (
                              <HubIcon 
                                style={{ 
                                  fontSize: 18, 
                                  color: '#0d47a1',
                                }} 
                              />
                            ) : (
                              <ApartmentIcon 
                                style={{ 
                                  fontSize: 18, 
                                  color: '#0d47a1',
                                }} 
                              />
                            )}
                          </div>
                        </Marker>
                      );
                    });
                })()}

                {/* County hover popup */}
                {popupInfo && !selectedCounty && (
                  <Popup
                    longitude={popupInfo.longitude}
                    latitude={popupInfo.latitude}
                    anchor="bottom"
                    closeButton={false}
                    closeOnClick={false}
                    className={classes.popup}
                  >
                    <div>
                      <div className={classes.popupCounty}>
                        {popupInfo.county_name}, {popupInfo.state}
                      </div>
                      <div className={classes.popupRow}>
                        <span>Population:</span>
                        <span>{popupInfo.population ? popupInfo.population.toLocaleString() : 'N/A'}</span>
                      </div>
                      <div className={classes.popupHint}>
                        Click to see {viewMode === 'origins' ? 'patient origins' : 'organizations'}
                      </div>
                    </div>
                  </Popup>
                )}

                {/* Origin county hover popup (when a county is selected in origins mode) */}
                {popupInfo && selectedCounty && viewMode === 'origins' && popupInfo.originPatients && (
                  <Popup
                    longitude={popupInfo.longitude}
                    latitude={popupInfo.latitude}
                    anchor="bottom"
                    closeButton={false}
                    closeOnClick={false}
                    className={classes.popup}
                  >
                    <div>
                      <div className={classes.popupCounty}>
                        {popupInfo.county_name}, {popupInfo.state}
                      </div>
                      <div className={classes.popupRow}>
                        <span>Captured Patients:</span>
                        <span>{popupInfo.originPatients.toLocaleString()}</span>
                      </div>
                    </div>
                  </Popup>
                )}

                {/* Organization popup */}
                {orgPopupInfo && (
                  <Popup
                    longitude={orgPopupInfo.longitude}
                    latitude={orgPopupInfo.latitude}
                    anchor="bottom"
                    closeButton={false}
                    closeOnClick={false}
                    className={classes.orgPopup}
                    offset={[0, -10]}
                  >
                    <div>
                      <div className={classes.orgPopupName}>
                        {orgPopupInfo.name}
                      </div>
                      <div className={classes.orgPopupRow}>
                        Captured Patients: {orgPopupInfo.patients?.toLocaleString() || 'N/A'}
                      </div>
                      <div className={classes.orgPopupLink}>
                        Click to view organization →
                      </div>
                    </div>
                  </Popup>
                )}
              </Map>

              {/* Selected county banner */}
              {selectedCounty && (
                <div className={classes.selectedCountyBanner}>
                  <Typography className={classes.selectedCountyText}>
                    {selectedCounty.name}, {selectedCounty.state}
                  </Typography>
                  {loadingOrgs ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Typography className={classes.clearButton} onClick={this.clearSelectedCounty}>
                      Clear
                    </Typography>
                  )}
                </div>
              )}

              {/* Initial instructions overlay */}
              {!selectedCounty && !popupInfo && (
                <div className={classes.instructions}>
                  <Typography className={classes.instructionsTitle}>
                    Patient Migration Explorer
                  </Typography>
                  {viewMode === 'destinations' ? (
                    <Typography className={classes.instructionsText}>
                      Hover over a county to see its population. Click on a county to view which organizations receive patients from that area.
                    </Typography>
                  ) : (
                    <Typography className={classes.instructionsText}>
                      Click on a county to see where patients at organizations in that county come from.
                    </Typography>
                  )}
                </div>
              )}

              {/* Legend */}
              {(viewMode === 'destinations' || selectedCounty) && (
              <div className={classes.legendContainer}>
                <div className={classes.legendTitle}>Legend</div>
                {serviceline && serviceline !== 'all' && (
                  <div className={classes.legendItem}>
                    <div 
                      className={classes.legendColor} 
                      style={{ backgroundColor: COUNTY_HAS_DATA_COLOR, opacity: 0.15 }}
                    />
                    <span>Has Migration Data</span>
                  </div>
                )}
                {viewMode === 'destinations' && (
                <div className={classes.legendItem}>
                  <div 
                    className={classes.legendColor} 
                    style={{ backgroundColor: COUNTY_SELECTED_COLOR, opacity: 0.7 }}
                  />
                  <span>Selected County</span>
                </div>
                )}
                {selectedCounty && viewMode === 'destinations' && (
                  <>
                    <div className={classes.legendItem}>
                      <div style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: colorScale[4],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <ApartmentIcon style={{ fontSize: 14, color: '#0d47a1' }} />
                      </div>
                      <span>Organization</span>
                    </div>
                    <div className={classes.legendItem}>
                      <div style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: colorScale[4],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <HubIcon style={{ fontSize: 14, color: '#0d47a1' }} />
                      </div>
                      <span>Affiliated Organization</span>
                    </div>
                    <div className={classes.legendItem}>
                      <div 
                        style={{ 
                          width: 60,
                          height: 12,
                          borderRadius: 2,
                          background: `linear-gradient(to right, ${colorScale.join(', ')})`,
                        }}
                      />
                      <span style={{ fontSize: '0.75rem' }}>Low → High Captured Patients</span>
                    </div>
                  </>
                )}
                {selectedCounty && viewMode === 'origins' && (
                  <div className={classes.legendItem}>
                    <div 
                      style={{ 
                        width: 60,
                        height: 12,
                        borderRadius: 2,
                        background: `linear-gradient(to right, ${colorScale.join(', ')})`,
                      }}
                    />
                    <span style={{ fontSize: '0.75rem' }}>Low → High Captured Patients</span>
                  </div>
                )}
              </div>
              )}
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }
}

PatientMigrationPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  router: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    clientId: state.app.selectedClient,
  };
}

const styled = withStyles(styles)(PatientMigrationPage);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as PatientMigrationPage };